<template>
  <v-dialog
    v-model="visible"
    class="project-modal"
    scrollable
    max-width="750"
  >
    <v-card
      v-if="action === 'delete'"
      class="pa-6"
    >
      <v-card-title primary-title>
        <div class="headline darkestblue--text">
          Are you sure?
        </div>
        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <div class="coolgray--text">
          <v-row>
            <v-col class="subtitle-2">
              ID
            </v-col>
            <v-col class="body-1">
              {{ entry.project_id }}
            </v-col>
          </v-row>

          <v-row>
            <v-col class="subtitle-2">
              Project
            </v-col>
            <v-col class="body-1">
              {{ entry.project_name }}
            </v-col>
          </v-row>

          <v-row>
            <v-col class="subtitle-2">
              Code
            </v-col>
            <v-col class="body-1">
              {{ entry.project_code }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          text
          color="coolgray"
          @click.stop="cancelAction"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          @click.stop="submitProjectAction"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      v-if="['create', 'update'].includes(action)"
      class="pa-4"
    >
      <v-card-title>
        <span>
          {{ action === 'create' ? 'Add' : 'Edit' }} Project
        </span>

        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="projectForm"
          v-model="validForm"
        >
          <v-container grid-list-xs>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="entry.project_name"
                  label="Project Name"
                  name="project_name"
                  type="textual"
                />

                <v-text-field
                  v-model="entry.project_code"
                  label="Project Code"
                  name="project_code"
                  type="textual"
                  width="150px"
                />

                <v-text-field
                  v-model="entry.billingCode"
                  label="Billing Code"
                  name="billingCode"
                  type="textual"
                  width="150px"
                />

                <v-text-field
                  v-model="entry.projectHours"
                  label="Project Hours"
                  name="projectHours"
                  type="number"
                  width="150px"
                />

                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="entry.startDate"
                      label="Start Date"
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="entry.startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>

                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="entry.endDate"
                      label="End Date"
                      prepend-icon="event"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="entry.endDate"
                    @input="endDateMenu = false"
                  />
                </v-menu>

                <!-- Select Project Type -->
                <v-select
                  v-model="entry.projectType"
                  :items="projectTypesWithCreateOption"
                  item-text="title"
                  item-value="value"
                  label="Project Type"
                  @change="onProjectTypeChange"
                />

                <v-textarea
                  v-model="entry.projectDescription"
                  auto-grow
                  class="border-light py-3"
                  counter="500"
                  label="Brief Description"
                  name="description"
                  type="textual"
                  :rules="descriptionRules"
                  row-height="15"
                  rows="1"
                  @input="validateForm"
                />

                <!-- Select Project Status -->
                <v-row>
                  <v-col>
                    <v-select
                      v-model="entry.projectStatus"
                      :items="projectStatuses"
                      item-text="title"
                      item-value="value"
                      label="Status"
                    />
                  </v-col>
                </v-row>

                <!-- Select Project Platforms -->
                <v-row>
                  <v-col>
                    <v-select
                      v-model="entry.projectPlatforms"
                      :items="projectPlatforms"
                      item-text="title"
                      item-value="value"
                      label="Platforms"
                      multiple
                      chips
                      deletable-chips
                      :hint="projectPlatforms.length === 0 ? 'No platforms available. Contact admin to add platforms.' : 'Select one or more platforms'"
                      persistent-hint
                    />
                  </v-col>
                </v-row>

                <v-row v-if="showThresholdFeature">
                  <v-col>
                    <v-btn
                      text
                      @click="showThresholdForm('create')"
                    >
                      <span>Add Threshold</span>
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Archive Project Option -->
                <v-row>
                  <v-col>
                    <v-checkbox
                      v-model="isProjectArchived"
                      label="Archive this project"
                      hint="Archived projects are hidden from active project lists"
                      persistent-hint
                      color="warning"
                    />
                  </v-col>
                </v-row>

                <!-- Deleted Project Option -->
                <v-row>
                  <v-col>
                    <v-checkbox
                      v-model="isProjectDeleted"
                      label="Mark as deleted"
                      hint="Deleted projects are marked for removal but data is preserved"
                      persistent-hint
                      color="error"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-row>
          <v-col>
            <v-card-actions class="justify-end">
              <v-btn
                text
                color="coolgray"
                @click.stop="cancelAction"
              >
                Cancel
              </v-btn>

              <v-btn
                color="primary"
                @click.stop="submitProjectAction"
              >
                Submit
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Create Project Type Dialog -->
    <v-dialog
      v-model="showCreateProjectTypeDialog"
      max-width="500"
      persistent
    >
      <v-card class="pa-4">
        <v-card-title>
          <span>Create Project Type</span>
          <v-spacer />
          <v-btn
            icon
            @click="closeCreateProjectTypeDialog"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form
            ref="projectTypeForm"
            v-model="validProjectTypeForm"
          >
            <v-text-field
              v-model="newProjectType.value"
              label="Value (required)"
              :rules="projectTypeValueRules"
              hint="Back-end value"
              persistent-hint
              required
            />

            <v-text-field
              v-model="newProjectType.title"
              label="Title"
              hint="Front-end label. Uses value if empty."
              persistent-hint
              :rules="projectTypeTitleRules"
            />

            <v-textarea
              v-model="newProjectType.description"
              label="Description"
              auto-grow
              rows="15"
              counter="500"
              :rules="projectTypeDescriptionRules"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            text
            color="coolgray"
            @click="closeCreateProjectTypeDialog"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            :loading="creatingProjectType"
            :disabled="!validProjectTypeForm"
            @click="createProjectType"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProjectModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    validForm: true,
    showThresholdFeature: false,
    thresholdForm: null,

    // Date picker menus
    startDateMenu: false,
    endDateMenu: false,

    // Project Type Creation
    showCreateProjectTypeDialog: false,
    validProjectTypeForm: false,
    creatingProjectType: false,
    newProjectType: {
      title: '',
      value: '',
      description: '',
    },
  }),
  computed: {
    visible: {
      get() {
        return this.isProjectModalVisible
      },
      set(value) {
        this.$store.commit('internal/isProjectModalVisible', value)
      },
    },
    action() {
      return this.$store.state.internal.action
    },
    didLoadProjectFail() {
      return this.$store.state.internal.didLoadProjectFail
    },
    entry() {
      return this.$store.state.internal.entry
    },
    isLoadingProject() {
      return this.$store.state.internal.isLoadingProject
    },
    isProjectModalVisible() {
      return this.$store.state.internal.isProjectModalVisible
    },
    projects() {
      return this.$store.state.internal.projects
    },
    selected() {
      return this.$store.state.internal.selected
    },
    errorMessage() {
      return this.$store.getters['internal/errorMessage']
    },
    successMessage() {
      return this.$store.getters['internal/successMessage']
    },
    projectTypes() {
      const adminProjectTypes = this.$store.getters['admin/projectTypes'] || []
      return adminProjectTypes.map((doc) => ({
        title: doc.title || doc.value,
        value: doc.value,
      }))
    },
    projectStatuses() {
      const adminProjectStatuses = this.$store.getters['admin/projectStatuses'] || []
      return adminProjectStatuses.map((doc) => ({
        title: doc.title || doc.value,
        value: doc.value,
      }))
    },
    projectPlatforms() {
      const adminProjectPlatforms = this.$store.getters['admin/projectPlatforms'] || []
      return adminProjectPlatforms.map((doc) => ({
        title: doc.title || doc.value,
        value: doc.value,
      }))
    },
    viewProject() {
      const {
        projects,
        didLoadProjectFail,
        isLoadingProject,
      } = this

      return isLoadingProject === false
        && didLoadProjectFail === false
        && projects.length > 0
    },
    noProjectData() {
      const {
        projects,
        isLoadingProject,
        didLoadProjectFail,
      } = this
      const requestComplete = isLoadingProject === false
      const requestSucceeded = didLoadProjectFail === false
      const noResults = projects.length === 0

      return requestComplete && requestSucceeded && noResults
    },
    descriptionRules() {
      return [
        (value) => {
          const valid = !value || value.length <= 500
          return valid || 'Description cannot exceed 500 characters.'
        },
      ]
    },
    projectTypesWithCreateOption() {
      return [
        ...this.projectTypes,
        {
          title: '+ Create New Project Type',
          value: '__create_new__',
        },
        {
          title: '⚙️ Manage Project Types',
          value: '__manage__',
        },
      ]
    },
    projectTypeTitleRules() {
      return [
        (value) => !value || value.length >= 2 || 'Title must be at least 2 characters if provided',
        (value) => !value || value.length <= 50 || 'Title must be 50 characters or less',
      ]
    },
    projectTypeValueRules() {
      return [
        (value) => !!value || 'Project type code is required',
        (value) => /^[a-z0-9-_]+$/.test(value) || 'Code can only contain lowercase letters, numbers, hyphens, and underscores',
        (value) => (value && value.length >= 2) || 'Code must be at least 2 characters',
        (value) => (value && value.length <= 30) || 'Code must be 30 characters or less',
        (value) => !this.projectTypes.some((pt) => pt.value === value) || 'This code already exists',
      ]
    },
    projectTypeDescriptionRules() {
      return [
        (value) => !value || value.length <= 500 || 'Description must be 500 characters or less',
      ]
    },
    isProjectArchived: {
      get() {
        return Boolean(this.entry && this.entry.isArchived)
      },
      set(value) {
        if (this.entry) {
          this.entry.isArchived = Boolean(value)
        }
      },
    },
    isProjectActive: {
      get() {
        return Boolean(this.entry && this.entry.isActive)
      },
      set(value) {
        if (this.entry) {
          this.entry.isActive = Boolean(value)
        }
      },
    },
    isProjectDeleted: {
      get() {
        return Boolean(this.entry && this.entry.isDeleted)
      },
      set(value) {
        if (this.entry) {
          this.entry.isDeleted = Boolean(value)
        }
      },
    },
  },
  async created() {
    // Fetch project types, statuses, and platforms from admin store
    await Promise.all([
      this.$store.dispatch('tools/fetchProjectTypes', undefined, { root: true }),
      this.$store.dispatch('tools/fetchProjectStatuses', undefined, { root: true }),
      this.$store.dispatch('tools/fetchProjectPlatforms', undefined, { root: true }),
    ])
  },
  methods: {
    onProjectTypeChange(value) {
      if (value === '__create_new__') {
        // Reset the selection and open the create dialog
        this.entry.projectType = null
        this.showCreateProjectTypeDialog = true
      } else if (value === '__manage__') {
        // Reset the selection and navigate to management page
        this.entry.projectType = null
        this.$router.push('/admin/projects')
      }
    },

    closeCreateProjectTypeDialog() {
      this.showCreateProjectTypeDialog = false
      this.resetProjectTypeForm()
    },

    resetProjectTypeForm() {
      this.newProjectType = {
        title: '',
        value: '',
        description: '',
      }
      if (this.$refs.projectTypeForm) {
        this.$refs.projectTypeForm.reset()
        this.$refs.projectTypeForm.resetValidation()
      }
    },

    async createProjectType() {
      if (!this.$refs.projectTypeForm.validate()) {
        return
      }

      this.creatingProjectType = true

      try {
        // Create the project type using the admin store
        const projectType = await this.$store.dispatch('admin/createProjectType', {
          title: this.newProjectType.title.trim(),
          value: this.newProjectType.value.trim(),
          description: this.newProjectType.description.trim(),
        })

        // Set the newly created project type as selected
        this.entry.projectType = projectType.value

        // Show success message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Project type "${projectType.title}" created successfully`,
          status: 'success',
        }, { root: true })

        // Close the dialog
        this.closeCreateProjectTypeDialog()
      } catch (error) {
        // Show error message
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Failed to create project type. Please try again.',
          status: 'error',
        }, { root: true })
      } finally {
        this.creatingProjectType = false
      }
    },
    validateForm() {
      return this.$refs.projectForm.validate()
    },

    reset() {
      return this.$refs.projectForm.reset()
    },

    resetValidation() {
      return this.$refs.projectForm.resetValidation()
    },

    closeForm() {
      this.resetValidation()
      this.visible = false
    },

    hideThresholdForm() {
      this.thresholdForm = null
    },
    showThresholdForm(action, entryThreshold = {}) {
      const testing = true

      if (testing) {
        return testing
      }

      if (action === 'update') {
        this.thresholdForm = entryThreshold
      } else {
        this.thresholdForm = {
          // Identifiers
          name: String,
          code: String, // User input of an alphanumeric value matching the TEST1234 pattern. Unique.

          // Change Logs
          updated: [{ date: Date, by: String }], // by === currentUser.email
          created: [{ date: Date, by: String }], // by === personUser.email

          // Dates
          startDate: Date,
          endDate: Date,

          watch: {
            collectionName: 'project', // Collection Name: String: [organization, person, project, service, team, time]
            property: 'projectCode', // Collection Code: String: [billingCode, personId, projectCode, serviceCode, teamCode]
            value: 'TEST1234', // A string to be used as a regex test on the given property, e.g. projectCode.
            operator: 'eq',
          },
          cause: [
            {
              dateRange: { startDate: Date, endDate: Date },
              // Update form input types and options based on context like page and user selections in the form.
              // Option Groups: [Boolean, Numerical, Textual, Strict (N/EQ), Custom]
              if: 'add', // Event: ['add', 'inactive', 'remove']
              is: true, // Value: Any: [user input, presets based on operator]
              for: 'yield partner', // ['demand source', 'network', 'yield partner']
              operator: 'and', // ['and', 'or']
            },
            {
              // @TODO Confirm operator at dateRange during user sessions.
              dateRange: { startDate: Date, endDate: Date }, // >= 14 days
              if: 'total',
              is: 0,
              for: 'delivery',
              operator: 'and', // ['and', 'or']
            },
          ],
          effect: [
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['accounts@adops.com', 'bts@adops.com'], // [personUser.email]
              frequency: 'daily', // Frequency Cap: [once, daily, weekly, monthly]
              schedule: 'immediately', // [immediately, daily, weekly, monthly]
            },
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['example.internal@adops.com'], // [personUser.email]
              frequency: 'once', // Frequency Cap: [daily, once]
              schedule: 'weekly', // [immediately, daily, weekly, monthly]
            },
          ],

          filter: [
            { operator: 'neq', property: 'name', value: 'Yield Partner A' },
            { operator: 'neq', property: 'type', value: 'Sponsorship' },
          ],
        }
      }

      return this.thresholdForm
    },
    hideProjectForm() {
      this.projectForm = null
    },
    showProjectForm(action, entryProject = {}) {
      if (action === 'update') {
        this.projectForm = entryProject
      } else {
        this.projectForm = {
          // Identifiers
          name: String,
          code: String,

          // Change Logs
          // Update change log separately to avoid performance issues.
          updated: [{
            // endpointName: 'project',
            // endpointCode: 'TEST2021',
            by: String, // by === currentUser.email
            date: Date,
            snapshot: String, // JSON.stringify(entry)
          }],

          // Dates
          startDate: Date,
          endDate: Date,

          contract: [
            {
              type: String, // []
              hours: Number,
            },
          ],
        }
      }
    },

    async submitProjectAction() {
      const { action, entry } = this
      const { commit, dispatch } = this.$store
      // @TODO: Actually map the action_type to what it's meant to be.
      const map = {
        create: 'ADD',
        update: 'UPDATE',
        delete: 'DELETE',
      }

      entry.action_type = map[action]

      /**
       * Send to Firebase.
       */

      const firestoreKeyMap = {
        projectCode: 'project_code',
        projectId: 'project_id',
        projectName: 'project_name',
      }
      const apiKeys = ['project_code', 'project_id', 'project_name']
      const firestoreKeys = Object.keys(firestoreKeyMap)
      const excludedProperties = [
        ...apiKeys,
        ...firestoreKeys,

        // Identifiers
        'docId',

        'created_dt',
        'updated_dt',

        // Data and Its Manipulation
        'action_type', // Used in API calls.
        'timeEntries', // Populated from Time API.
        'normalized', // Filter and sort helper.
        'notes', // Populated from Notes Collection in Firestore.

        // View State
        'isLoadingTimeEntries',

        // Calculated Fields
        'isActiveColor',
        'isActiveLabel',
        'projectDaysRemaining',
        'projectHref',
        'projectLength',

        'projectStatusColorBackground',
        'projectStatusColorText',

        // Formatted Dates
        'createdDateFormatted',
        'startDateFormatted',
        'endDateFormatted',
        'updatedDateFormatted',
      ]
      const firebaseDoc = Object.keys(entry).reduce((obj, key) => {
        const isFirestoreKey = firestoreKeyMap[key] !== undefined
        const isExcludedKey = excludedProperties.includes(key)

        if (isFirestoreKey) {
          const apiKey = `${firestoreKeyMap[key]}`

          obj[key] = entry[apiKey]
        }

        if (!isExcludedKey && entry[key] !== undefined && entry[key] !== null) {
          obj[key] = entry[key]
        }

        return obj
      }, {})

      // Upsert the document to Firestore.
      await this.$store.dispatch('internal/upsertProjectToFirestore', firebaseDoc)

      /**
       * Send to API.
       */

      const apiResponse = await dispatch('internal/submitProjectAction', entry)
      const { content: apiContent = [], error: apiError } = apiResponse || {}

      if (apiError || apiContent?.code || !apiResponse) {
        const message = apiError?.message || apiContent?.message || apiContent?.code || 'Unknown API Error'

        commit('tools/snackbar', true)
        commit('tools/snack', { message, status: 'error' })

        return apiError || apiContent
      }

      const [entryId] = apiContent
      const apiStatus = entryId ? 'success' : 'error'
      const message = entryId ? this.successMessage : this.errorMessage

      if (apiStatus === 'success') {
        this.closeForm()
      }

      commit('tools/snackbar', true)
      commit('tools/snack', { message, status: apiStatus })

      return apiResponse
    },
    cancelAction() {
      const { selected } = this

      this.$store.commit('internal/action', 'read')
      this.$store.commit('internal/entry', Object.create(selected))

      this.closeForm()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

::v-deep.v-card {
  border-radius: 6px;
  border-width: 0;
}

::v-deep.v-card__title {
  color: $color-monochrome-darkestblue;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.51px;
  line-height: 22px;
  justify-content: space-between;
}

::v-deep.v-card__text {
  padding: 0;
}

.v-card + .v-card {
  margin-top: 1ch;
}
</style>
